import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  ButtonGroup,
  Button,
  styled,
  Divider,
  TextField,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';

// Material UI Icons
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import PublishIcon from '@mui/icons-material/Publish';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Styled components
const StyledToolbar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: '4px',
  padding: '4px 12px',
  fontSize: '0.875rem',
  minWidth: 'auto',
  gap: '6px'
}));

const SearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    height: '36px',
    fontSize: '0.875rem',
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
  }
}));

const TableContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 'calc(100% - 64px)',
  overflow: 'auto',
  '& table': {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  '& th': {
    padding: '8px 16px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    textAlign: 'left',
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  '& td': {
    padding: '8px 16px',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: theme.palette.text.secondary,
}));

const Pagination = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const AssetManagementGrid = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Column definitions matching the image
  const columns = [
    { id: 'tag', label: 'Tag' },
    { id: 'model', label: 'Model' },
    { id: 'description', label: 'Description' },
    { id: 'abbreviation', label: 'Abbreviation' },
    { id: 'barCode', label: 'Bar Code' },
    { id: 'partNumber', label: 'Part Number' },
    { id: 'status', label: 'Status' },
    { id: 'parentAsset', label: 'Parent Asset' },
    { id: 'workMode', label: 'Work Mode' },
    { id: 'purchaseOrder', label: 'Purchase Order' },
    { id: 'department', label: 'Department' },
    { id: 'building', label: 'Building' },
    { id: 'zone', label: 'Zone' },
    { id: 'location', label: 'Location' }
  ];

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        overflow: 'hidden'
      }}
    >
      {/* Toolbar */}
      <StyledToolbar>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <ActionButton variant="contained" color="primary" startIcon={<AddCircleIcon fontSize="small" />}>
            Add
          </ActionButton>
          <ActionButton variant="outlined" startIcon={<EditIcon fontSize="small" />}>
            Edit
          </ActionButton>
          <ActionButton variant="outlined" startIcon={<DeleteIcon fontSize="small" />}>
            Delete
          </ActionButton>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <ActionButton variant="outlined" startIcon={<PublishIcon fontSize="small" />}>
            Import
          </ActionButton>
          <ActionButton variant="outlined" startIcon={<GetAppIcon fontSize="small" />}>
            Export
          </ActionButton>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <SearchField
            placeholder="Search..."
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <Tooltip title="Filter">
            <IconButton size="small">
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Refresh">
            <IconButton size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Settings">
            <IconButton size="small">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="More options">
            <IconButton 
              size="small"
              onClick={handleMenuClick}
              aria-controls={open ? "more-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            id="more-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'more-button',
            }}
          >
            <MenuItem onClick={handleMenuClose}>Bulk Edit</MenuItem>
            <MenuItem onClick={handleMenuClose}>Print</MenuItem>
            <MenuItem onClick={handleMenuClose}>Archive</MenuItem>
          </Menu>
        </Box>
      </StyledToolbar>
      
      {/* Table */}
      <TableContainer>
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {column.label}
                    <ArrowDropDownIcon fontSize="small" />
                  </Box>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* This is intentionally empty to match the "No rows" display in the image */}
          </tbody>
        </table>
        
        <EmptyState>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>No rows</Typography>
        </EmptyState>
      </TableContainer>
      
      {/* Pagination */}
      <Pagination>
        <Box>Rows per page:</Box>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          <Typography variant="body2" sx={{ mr: 1 }}>100</Typography>
          <ArrowDropDownIcon fontSize="small" />
        </Box>
        <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
        <Box>0–0 of 0</Box>
      </Pagination>
    </Paper>
  );
};

export default AssetManagementGrid;